import { putUserLanguage } from '@/api/requests/requests';
import { useResetAppState } from '@/setup/SessionProvider';
import { ArrowRight, KeyboardArrowRight, Logout } from '@mui/icons-material';
import { Box } from '@mui/system';
import { useUIDrawer } from 'api/queries/ui';
import { useUsersCurrentAccessesQuery } from 'api/queries/users';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AlertIcon,
  BarChartIcon,
  BriefcaseIcon,
  BusIcon,
  CalendarIcon,
  CoinIcon,
  DocumentIcon,
  DrawerLanguagePicker,
  GearIcon,
  GlobeIcon,
  HomeIcon,
  LanguageOptions,
  ListItemLink,
  MenuItem,
  MenuItemWithLangKey,
  MessagesIcon,
  UserPermission,
  UsersIcon,
  colors,
  extendMenuItemByKey,
} from 'remote/global';
import { ROUTES, RouteItemKey } from '../data/routes';
import { track } from '../tracking';

export function useLanguageMenuItems() {
  const langMenuItems: MenuItemWithLangKey<RouteItemKey>[] = [
    {
      itemKey: ROUTES.LanguageRoutes.LANGUAGE_ET,
      langKey: LanguageOptions.et,
      label: 'Eesti',
      icon: <ArrowRight fontSize='small' />,
      permission: UserPermission.WRITE_ALL,
    },
    {
      itemKey: ROUTES.LanguageRoutes.LANGUAGE_EN,
      langKey: LanguageOptions.en,
      label: 'English',
      icon: <ArrowRight fontSize='small' />,
      permission: UserPermission.WRITE_ALL,
    },
    {
      itemKey: ROUTES.LanguageRoutes.LANGUAGE_LV,
      langKey: LanguageOptions.lv,
      label: 'Latviski',
      icon: <ArrowRight fontSize='small' />,
      permission: UserPermission.WRITE_ALL,
    },
    {
      itemKey: ROUTES.LanguageRoutes.LANGUAGE_RU,
      langKey: LanguageOptions.ru,
      label: 'Русский',
      icon: <ArrowRight fontSize='small' />,
      permission: UserPermission.WRITE_ALL,
    },
  ];

  return langMenuItems;
}

function useMenuItems(options?: { alertCount?: number }) {
  const { data: userInfo } = useUsersCurrentAccessesQuery();
  const { setIsMobileDrawerOpen } = useUIDrawer();
  const tInstance = useTranslation();
  const { permissions = {} } = userInfo ?? {};
  const location = useLocation();
  const { t } = tInstance;
  const langMenuItems = useLanguageMenuItems();
  const resetState = useResetAppState();
  const navigate = useNavigate();
  const userId = userInfo?.userId;

  const menuItems: MenuItem<RouteItemKey>[][] = [
    [
      {
        itemKey: ROUTES.MainRoutes.DASHBOARD,
        label: t('Menu.Navigation.main.dashboard.title'),
        to: [ROUTES.BaseRoutes.MAIN, ROUTES.MainRoutes.DASHBOARD].join('/'),
        icon: <HomeIcon />,
        permission: UserPermission.WRITE_ALL,
        onClick: () =>
          track.partner_menu_click([ROUTES.BaseRoutes.MAIN, ROUTES.MainRoutes.DASHBOARD].join('/')),
      },
      {
        itemKey: ROUTES.MainRoutes.AVAILABILITY,
        label: t('Menu.Navigation.main.availability.title'),
        to: [ROUTES.BaseRoutes.MAIN, ROUTES.MainRoutes.AVAILABILITY].join('/'),
        icon: <CalendarIcon />,
        permission: UserPermission.WRITE_ALL,
        onClick: () =>
          track.partner_menu_click(
            [ROUTES.BaseRoutes.MAIN, ROUTES.MainRoutes.AVAILABILITY].join('/')
          ),
        items: [
          {
            itemKey: ROUTES.AvailabilityRoutes.BUS,
            label: t('Menu.Navigation.main.availability.bus.title'),
            to: [
              ROUTES.BaseRoutes.MAIN,
              ROUTES.MainRoutes.AVAILABILITY,
              ROUTES.AvailabilityRoutes.BUS,
            ].join('/'),
            icon: <KeyboardArrowRight fontSize='small' />,
            permission: permissions.VEHICLE_SCHEDULE,
            onClick: () =>
              track.partner_menu_click(
                [
                  ROUTES.BaseRoutes.MAIN,
                  ROUTES.MainRoutes.AVAILABILITY,
                  ROUTES.AvailabilityRoutes.BUS,
                ].join('/')
              ),
          },
          {
            itemKey: ROUTES.AvailabilityRoutes.DRIVER,
            label: t('Menu.Navigation.main.availability.driver.title'),
            to: [
              ROUTES.BaseRoutes.MAIN,
              ROUTES.MainRoutes.AVAILABILITY,
              ROUTES.AvailabilityRoutes.DRIVER,
            ].join('/'),
            icon: <KeyboardArrowRight fontSize='small' />,
            permission: permissions.DRIVER_SCHEDULE,
            onClick: () =>
              track.partner_menu_click(
                [
                  ROUTES.BaseRoutes.MAIN,
                  ROUTES.MainRoutes.AVAILABILITY,
                  ROUTES.AvailabilityRoutes.DRIVER,
                ].join('/')
              ),
          },
        ],
      },
      {
        itemKey: ROUTES.MainRoutes.OFFERS,
        label: t('Menu.Navigation.main.offers.title'),
        to: [ROUTES.BaseRoutes.MAIN, ROUTES.MainRoutes.OFFERS].join('/'),
        icon: <BarChartIcon />,
        permission: permissions.OPERATOR_OFFER,
        onClick: () =>
          track.partner_menu_click([ROUTES.BaseRoutes.MAIN, ROUTES.MainRoutes.OFFERS].join('/')),
      },
      {
        itemKey: ROUTES.MainRoutes.LOGISTICS,
        label: t('Menu.Navigation.main.logistics.title'),
        to: [ROUTES.BaseRoutes.MAIN, ROUTES.MainRoutes.LOGISTICS].join('/'),
        icon: <GlobeIcon />,
        permission: UserPermission.WRITE_ALL,
        onClick: () =>
          track.partner_menu_click([ROUTES.BaseRoutes.MAIN, ROUTES.MainRoutes.LOGISTICS].join('/')),
        items: [
          {
            itemKey: ROUTES.LogisticsRoutes.ALL_RIDES,
            label: t('Menu.Navigation.main.logistics.allRides.title'),
            to: [
              ROUTES.BaseRoutes.MAIN,
              ROUTES.MainRoutes.LOGISTICS,
              ROUTES.LogisticsRoutes.ALL_RIDES,
            ].join('/'),
            icon: <KeyboardArrowRight fontSize='small' />,
            permission: permissions.RIDE,
            onClick: () =>
              track.partner_menu_click(
                [
                  ROUTES.BaseRoutes.MAIN,
                  ROUTES.MainRoutes.LOGISTICS,
                  ROUTES.LogisticsRoutes.ALL_RIDES,
                ].join('/')
              ),
          },
          {
            itemKey: ROUTES.LogisticsRoutes.MAP,
            label: t('Menu.Navigation.main.logistics.map.title'),
            to: [
              ROUTES.BaseRoutes.MAIN,
              ROUTES.MainRoutes.LOGISTICS,
              ROUTES.LogisticsRoutes.MAP,
            ].join('/'),
            icon: <KeyboardArrowRight fontSize='small' />,
            permission: permissions.VEHICLE_LOCATION,
            onClick: () =>
              track.partner_menu_click(
                [
                  ROUTES.BaseRoutes.MAIN,
                  ROUTES.MainRoutes.LOGISTICS,
                  ROUTES.LogisticsRoutes.MAP,
                ].join('/')
              ),
          },
        ],
      },
      {
        itemKey: ROUTES.MainRoutes.FLEET,
        label: t('Menu.Navigation.main.fleet.title'),
        to: [ROUTES.BaseRoutes.MAIN, ROUTES.MainRoutes.FLEET].join('/'),
        icon: <BusIcon />,
        permission: permissions.VEHICLE_DETAILS,
        onClick: () =>
          track.partner_menu_click([ROUTES.BaseRoutes.MAIN, ROUTES.MainRoutes.FLEET].join('/')),
      },
      {
        itemKey: ROUTES.MainRoutes.DRIVERS,
        label: t('Menu.Navigation.main.drivers.title'),
        to: [ROUTES.BaseRoutes.MAIN, ROUTES.MainRoutes.DRIVERS].join('/'),
        icon: <UsersIcon />,
        permission: permissions.DRIVER_DETAILS,
        onClick: () =>
          track.partner_menu_click([ROUTES.BaseRoutes.MAIN, ROUTES.MainRoutes.DRIVERS].join('/')),
      },
      {
        itemKey: ROUTES.MainRoutes.PRICING,
        label: t('Menu.Navigation.main.pricing.title'),
        to: [ROUTES.BaseRoutes.MAIN, ROUTES.MainRoutes.PRICING].join('/'),
        icon: <CoinIcon />,
        permission: permissions.PRICING,
        onClick: () =>
          track.partner_menu_click([ROUTES.BaseRoutes.MAIN, ROUTES.MainRoutes.PRICING].join('/')),
        items: [
          {
            itemKey: ROUTES.PricingRoutes.COST,
            label: t('Menu.Navigation.main.pricing.cost.title'),
            to: [ROUTES.BaseRoutes.MAIN, ROUTES.MainRoutes.PRICING, ROUTES.PricingRoutes.COST].join(
              '/'
            ),
            icon: <KeyboardArrowRight fontSize='small' />,
            permission: permissions.PRICING,
            onClick: () =>
              track.partner_menu_click(
                [ROUTES.BaseRoutes.MAIN, ROUTES.MainRoutes.PRICING, ROUTES.PricingRoutes.COST].join(
                  '/'
                )
              ),
          },
          {
            itemKey: ROUTES.PricingRoutes.PERIOD,
            label: t('Menu.Navigation.main.pricing.period.title'),
            to: [
              ROUTES.BaseRoutes.MAIN,
              ROUTES.MainRoutes.PRICING,
              ROUTES.PricingRoutes.PERIOD,
            ].join('/'),
            icon: <KeyboardArrowRight fontSize='small' />,
            permission: permissions.PRICING,
            onClick: () =>
              track.partner_menu_click(
                [
                  ROUTES.BaseRoutes.MAIN,
                  ROUTES.MainRoutes.PRICING,
                  ROUTES.PricingRoutes.PERIOD,
                ].join('/')
              ),
          },
        ],
      },
      {
        itemKey: ROUTES.MainRoutes.B2B,
        label: t('Menu.Navigation.main.b2b.my-clients.title'),
        to: [ROUTES.BaseRoutes.MAIN, ROUTES.MainRoutes.B2B, ROUTES.B2BRoutes.MY_CLIENTS].join('/'),
        icon: <BriefcaseIcon />,
        permission: permissions.OPERATOR_CLIENT,
        onClick: () =>
          track.partner_menu_click(
            [ROUTES.BaseRoutes.MAIN, ROUTES.MainRoutes.B2B, ROUTES.B2BRoutes.MY_CLIENTS].join('/')
          ),
      },
      {
        itemKey: ROUTES.MainRoutes.ALERTS,
        label: (
          <Box component='p' sx={{ m: 0 }}>
            {t('Menu.Navigation.main.alerts.title')}
            {options?.alertCount != null && options.alertCount > 0 && (
              <Box
                component='span'
                sx={{
                  borderRadius: '4px',
                  backgroundColor: colors.restYellow,
                  color: '#FFF',
                  px: 0.5,
                  py: 0.2,
                  ml: 1,
                  fontSize: '12px',
                }}
              >
                {options?.alertCount}
              </Box>
            )}
          </Box>
        ),
        to: [ROUTES.BaseRoutes.MAIN, ROUTES.MainRoutes.ALERTS].join('/'),
        icon: <AlertIcon />,
        permission: permissions.ALERT,
        onClick: () =>
          track.partner_menu_click([ROUTES.BaseRoutes.MAIN, ROUTES.MainRoutes.ALERTS].join('/')),
        items: [
          {
            itemKey: ROUTES.AlertsRoutes.CURRENT,
            label: t('Menu.Navigation.main.alerts.current.title'),
            to: [
              ROUTES.BaseRoutes.MAIN,
              ROUTES.MainRoutes.ALERTS,
              ROUTES.AlertsRoutes.CURRENT,
            ].join('/'),
            icon: <KeyboardArrowRight fontSize='small' />,
            permission: permissions.ALERT,
            onClick: () =>
              track.partner_menu_click(
                [
                  ROUTES.BaseRoutes.MAIN,
                  ROUTES.MainRoutes.ALERTS,
                  ROUTES.AlertsRoutes.CURRENT,
                ].join('/')
              ),
          },
          {
            itemKey: ROUTES.AlertsRoutes.HISTORY,
            label: t('Menu.Navigation.main.alerts.history.title'),
            to: [
              ROUTES.BaseRoutes.MAIN,
              ROUTES.MainRoutes.ALERTS,
              ROUTES.AlertsRoutes.HISTORY,
            ].join('/'),
            icon: <KeyboardArrowRight fontSize='small' />,
            permission: permissions.ALERT,
            onClick: () =>
              track.partner_menu_click(
                [
                  ROUTES.BaseRoutes.MAIN,
                  ROUTES.MainRoutes.ALERTS,
                  ROUTES.AlertsRoutes.HISTORY,
                ].join('/')
              ),
          },
        ],
      },
      {
        itemKey: ROUTES.MainRoutes.MESSAGES,
        label: t('Menu.Navigation.main.messages.title'),
        to: [ROUTES.BaseRoutes.MAIN, ROUTES.MainRoutes.MESSAGES].join('/'),
        icon: <MessagesIcon />,
        permission: permissions.USER_MESSAGE,
        onClick: () =>
          track.partner_menu_click([ROUTES.BaseRoutes.MAIN, ROUTES.MainRoutes.MESSAGES].join('/')),
        items: [
          {
            itemKey: ROUTES.MessagesRoutes.HISTORY,
            label: t('Menu.Navigation.main.messages.history.title'),
            to: [
              ROUTES.BaseRoutes.MAIN,
              ROUTES.MainRoutes.MESSAGES,
              ROUTES.MessagesRoutes.HISTORY,
            ].join('/'),
            icon: <KeyboardArrowRight fontSize='small' />,
            permission: permissions.USER_MESSAGE,
            onClick: () =>
              track.partner_menu_click(
                [
                  ROUTES.BaseRoutes.MAIN,
                  ROUTES.MainRoutes.MESSAGES,
                  ROUTES.MessagesRoutes.HISTORY,
                ].join('/')
              ),
          },
          {
            itemKey: ROUTES.MessagesRoutes.GROUP,
            label: t('Menu.Navigation.main.messages.group.title'),
            to: [
              ROUTES.BaseRoutes.MAIN,
              ROUTES.MainRoutes.MESSAGES,
              ROUTES.MessagesRoutes.GROUP,
            ].join('/'),
            icon: <KeyboardArrowRight fontSize='small' />,
            permission: permissions.USER_MESSAGE_GROUP,
            onClick: () =>
              track.partner_menu_click(
                [
                  ROUTES.BaseRoutes.MAIN,
                  ROUTES.MainRoutes.MESSAGES,
                  ROUTES.MessagesRoutes.GROUP,
                ].join('/')
              ),
          },
        ],
      },
    ],
    [
      {
        itemKey: ROUTES.MainRoutes.LANGUAGE,
        label: t('Menu.Navigation.main.language.title'),
        // This component will be added afterwards as this component needs to reference this same object
        component: <div />,
        icon: <GlobeIcon />,
        permission: UserPermission.WRITE_ALL,
        onClick: () => track.partner_menu_click(ROUTES.MainRoutes.LANGUAGE),
      },
      {
        itemKey: ROUTES.MainRoutes.DOCUMENTATION,
        label: t('Menu.Navigation.main.documentation.title'),
        to: [ROUTES.BaseRoutes.MAIN, ROUTES.MainRoutes.DOCUMENTATION].join('/'),
        icon: <DocumentIcon />,
        permission: UserPermission.WRITE_ALL,
        onClick: () =>
          track.partner_menu_click(
            [ROUTES.BaseRoutes.MAIN, ROUTES.MainRoutes.DOCUMENTATION].join('/')
          ),
      },
      {
        itemKey: ROUTES.MainRoutes.SETTINGS,
        label: t('Menu.Navigation.main.settings.title'),
        to: [ROUTES.BaseRoutes.MAIN, ROUTES.MainRoutes.SETTINGS].join('/'),
        icon: <GearIcon />,
        permission: permissions.USER_ACCOUNT,
        onClick: () =>
          track.partner_menu_click([ROUTES.BaseRoutes.MAIN, ROUTES.MainRoutes.SETTINGS].join('/')),
        items: [
          {
            itemKey: ROUTES.SettingsRoutes.ACCOUNT,
            label: t('Menu.Navigation.main.settings.account.title'),
            to: [
              ROUTES.BaseRoutes.MAIN,
              ROUTES.MainRoutes.SETTINGS,
              ROUTES.SettingsRoutes.ACCOUNT,
            ].join('/'),
            icon: <KeyboardArrowRight fontSize='small' />,
            permission: permissions.USER_ACCOUNT,
            onClick: () =>
              track.partner_menu_click(
                [
                  ROUTES.BaseRoutes.MAIN,
                  ROUTES.MainRoutes.SETTINGS,
                  ROUTES.SettingsRoutes.ACCOUNT,
                ].join('/')
              ),
          },
          {
            itemKey: ROUTES.SettingsRoutes.SUBSCRIPTION,
            label: t('Menu.Navigation.main.settings.subscription.title'),
            to: [
              ROUTES.BaseRoutes.MAIN,
              ROUTES.MainRoutes.SETTINGS,
              ROUTES.SettingsRoutes.SUBSCRIPTION,
            ].join('/'),
            icon: <KeyboardArrowRight fontSize='small' />,
            permission: permissions.OPERATOR_PLAN,
            onClick: () =>
              track.partner_menu_click(
                [
                  ROUTES.BaseRoutes.MAIN,
                  ROUTES.MainRoutes.SETTINGS,
                  ROUTES.SettingsRoutes.SUBSCRIPTION,
                ].join('/')
              ),
          },
          {
            itemKey: ROUTES.SettingsRoutes.COMPANY,
            label: t('Menu.Navigation.main.settings.company.title'),
            to: [
              ROUTES.BaseRoutes.MAIN,
              ROUTES.MainRoutes.SETTINGS,
              ROUTES.SettingsRoutes.COMPANY,
            ].join('/'),
            icon: <KeyboardArrowRight fontSize='small' />,
            permission: permissions.OPERATOR_FULL_DETAILS,
            onClick: () =>
              track.partner_menu_click(
                [
                  ROUTES.BaseRoutes.MAIN,
                  ROUTES.MainRoutes.SETTINGS,
                  ROUTES.SettingsRoutes.COMPANY,
                ].join('/')
              ),
          },
          {
            itemKey: ROUTES.SettingsRoutes.USERS,
            label: t('Menu.Navigation.main.settings.users.title'),
            to: [
              ROUTES.BaseRoutes.MAIN,
              ROUTES.MainRoutes.SETTINGS,
              ROUTES.SettingsRoutes.USERS,
            ].join('/'),
            icon: <KeyboardArrowRight fontSize='small' />,
            permission: permissions.OPERATOR_USER,
            onClick: () =>
              track.partner_menu_click(
                [
                  ROUTES.BaseRoutes.MAIN,
                  ROUTES.MainRoutes.SETTINGS,
                  ROUTES.SettingsRoutes.USERS,
                ].join('/')
              ),
          },
          // Temporarily disabled, will be used in the future
          /* {
            itemKey: ROUTES.SettingsRoutes.CONFIGURATION,
            label: t('Menu.Navigation.main.settings.configuration.title'),
            to: [
              ROUTES.BaseRoutes.MAIN,
              ROUTES.MainRoutes.SETTINGS,
              ROUTES.SettingsRoutes.CONFIGURATION,
            ].join('/'),
            icon: <KeyboardArrowRight fontSize='small' />,
            permission: permissions.OPERATOR_USER,
          }, */
        ],
      },
      {
        itemKey: ROUTES.MainRoutes.LOGOUT,
        label: t('Menu.Navigation.main.logout.title'),
        component: <div />,
        icon: <Logout fontSize='small' />,
        permission: UserPermission.WRITE_ALL,
        onClick: () => track.partner_menu_click(ROUTES.MainRoutes.LOGOUT),
      },
    ],
  ];

  const menuItemsWithComponents = [
    {
      key: ROUTES.MainRoutes.LANGUAGE,
      component: (
        <DrawerLanguagePicker<RouteItemKey>
          setIsMobileDrawerOpen={setIsMobileDrawerOpen}
          langMenuItemKey={ROUTES.MainRoutes.LANGUAGE}
          menuItems={menuItems}
          languageMenuItems={langMenuItems}
          onLanguageChange={async (newLang: LanguageOptions) => {
            if (userId) {
              await putUserLanguage(String(userId), newLang);
            }
          }}
        />
      ),
    },
    {
      key: ROUTES.MainRoutes.LOGOUT,
      component: (
        <ListItemLink
          item={
            menuItems.flatMap((el) => el).find((el) => el.itemKey === ROUTES.MainRoutes.LOGOUT)!
          }
          onClick={() => {
            resetState();
            navigate(ROUTES.BaseRoutes.LOGIN, {
              replace: true,
              state: { from: location.pathname },
            });
            track.partner_logout();
          }}
        />
      ),
    },
  ];

  const result = menuItemsWithComponents.reduce(
    (acc, { key, ...rest }) => extendMenuItemByKey(acc, key, { ...rest }),
    menuItems
  );

  return result;
}

export default useMenuItems;
